import {
    columnConfigs,
    type ColumnOption,
    type EmployeeSortProperty,
    formatDate,
    mergeWithDefaultColumns,
    serializeColumOptions,
    sortBy,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/column-selector";
import type { ColumnSelectorChangedEvent } from "../../../elements/column-selector";
import "../../../elements/popover";
import { app, appMode } from "../../../init";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../employees-filter";

// @TODO: align with single name 'core_data'
@customElement("ptc-employees-profile-all")
export class EmployeesProfileAll extends Routing(StateMixin(LitElement)) {
    routePattern = /employees\/all\/core-data/;

    get routeTitle() {
        return "Stammdaten: Alle Mitarbeiter";
    }

    // TODO: refactor this and also type ColumnOption from excel export?
    private static _defaultColumns: ColumnOption[] = [
        { type: "lastName", order: 0 },
        { type: "firstName", order: 1 },
        { type: "callName", order: 2 },
        { type: "staffNumber", order: 3 },
        { type: "birthday", order: 4 },
        { type: "address", order: 5 },
        { type: "postalCode", order: 6 },
        { type: "city", order: 7 },
        { type: "email", order: 8 },
        { type: "phone", order: 9 },
        { type: "phone2", order: 10 },
        { type: "timePin", order: 11 },
    ];

    @state()
    private _loading: boolean = false;

    @state()
    private _displayColumns: ColumnOption[] =
        app.settings.coreDataColumns.length > 0
            ? mergeWithDefaultColumns(app.settings.coreDataColumns, EmployeesProfileAll._defaultColumns)
            : EmployeesProfileAll._defaultColumns;

    private get _employees() {
        return app.getFilteredEmployees();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            .row {
                display: flex;
                page-break-inside: avoid;
            }

            .row:not(.header):hover {
                cursor: pointer;
                color: var(--color-primary);
            }

            .row.header {
                font-weight: bold;
                position: sticky;
                top: 0;
                z-index: 2;
            }

            .row > * {
                width: 10em;
                padding: 0.5em;
                flex: none;
                word-break: break-word;
                border-bottom: solid 1px var(--shade-2);
                background: var(--color-bg);
            }

            .row :not(:last-child) {
                border-right: solid 1px var(--shade-2);
            }

            .row > .lastName {
                position: sticky;
                left: 0;
                z-index: 1;
            }

            .row > .firstName {
                width: 7em;
            }

            .row > .callName {
                width: 5em;
            }

            .row > .staffNumber {
                width: 5em;
            }

            .row > .timePin {
                width: 5em;
            }

            .row > .postalCode {
                width: 5em;
            }

            .row > .birthday {
                width: 6em;
            }

            .row > .phone,
            .row > .phone2 {
                width: 8em;
            }

            .row > .email {
                width: 15em;
            }

            .row > .address {
                width: 15em;
            }

            .row > .avatar {
                width: 2em;
                border: none;
                padding: 0.3em;
            }

            .row > .avatar ptc-ui-avatar {
                font-size: var(--font-size-tiny);
            }

            @media print {
                .row {
                    font-size: var(--font-size-tiny);
                }

                .wrapper {
                    position: static;
                    display: block;
                }
            }
        `,
    ];

    render() {
        const columns = sortBy(
            this._displayColumns.filter((column) => !column.disabled),
            (column) => column.order ?? Number.MAX_SAFE_INTEGER,
            "ascending"
        );

        return html`
            <div class="fullbleed vertical layout wrapper">
                <div class="padded spacing center-aligning horizontal layout border-bottom noprint">
                    <div class="stretch"></div>

                    <ptc-column-selector
                        .columns=${this._displayColumns}
                        @changed=${(e: ColumnSelectorChangedEvent) => {
                            this._displayColumns = e.detail.columns;
                            void app.updateSettings({ coreDataColumns: e.detail.columns });
                        }}
                    ></ptc-column-selector>

                    <button
                        class="slim transparent"
                        title="Stammdaten als Excel-Datei Exportieren"
                        @click="${() => {
                            this.go("exports/employee-table-excel", {
                                "entity-filters": JSON.stringify(app.employeeFilters.filters),
                                columns: serializeColumOptions(this._displayColumns),
                            });
                        }}"
                    >
                        <ptc-ui-icon name="icon.file.excel"></ptc-ui-icon>
                    </button>
                    <button
                        class="slim transparent"
                        title="Stammdaten als PDF-Datei Exportieren"
                        @click="${() => print()}"
                        ?hidden=${appMode === "embedded"}
                    >
                        <ptc-ui-icon name="icon.action.print"></ptc-ui-icon>
                    </button>
                </div>

                <div class="padded horizontal layout small-caps bottom-margined printonly">
                    <div class="stretch">Personal-Stammdaten (Stand ${formatDate(new Date())})</div>
                    <div class="subtle">${app.company?.name}</div>
                </div>

                <ptc-employees-filter
                    class="border-bottom"
                    .sortableProps=${[
                        "firstName",
                        "lastName",
                        "staffNumber",
                        "birthday",
                        "birthdayDate",
                        "email",
                    ] as EmployeeSortProperty[]}
                ></ptc-employees-filter>

                <div class="scroller stretch">
                    <div class="header row">
                        <div class="avatar noprint"></div>
                        ${columns.map(
                            (column) =>
                                html`<div class="${column.type}">${columnConfigs[column.type].getHeader()}</div>`
                        )}
                    </div>

                    ${this._employees.map((employee) => {
                        return html`
                            <div class="employee row" @click=${() => this.go(`employees/${employee.id}/core-data`)}>
                                <div class="avatar left-padded centering layout noprint">
                                    <ptc-ui-avatar
                                        .employee=${employee}
                                        .positionColors=${employee.positions.map((p) =>
                                            app.company.getPositionColor(p)
                                        )}
                                    ></ptc-ui-avatar>
                                </div>
                                ${columns.map(
                                    (column) =>
                                        html`<div class="${column.type}">
                                            ${columnConfigs[column.type].getValue({ employee }, column)}
                                        </div>`
                                )}
                            </div>
                        `;
                    })}
                </div>

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
